import { Component, Prop, h, Element, State } from '@stencil/core';

const COMPONENT_NAME = 'faq-type-2';
/**
 * FAQ accordion component (type-2) that displays frequently asked questions
 * in an expandable format with a centered header, footer, and a one-column layout on desktop.
 *
 * @slot heading - <me-heading tag="h2" variant="2" align="center">
 * @slot subheading - <me-text tag="p" variant="large" align="center">
 * @slot action-primary - <me-button variant="secondary">
 *
 * Features:
 * - Left-side header with title and subtitle
 * - Right-side accordion container
 * - Accordion items with border-top only
 * - Accordion items with all sides border
 * - Single column layout for mobile
 *
 * - Without footer
 *
 * @category FAQs
 * @example
 *   <faq-type-2 items='[{"question": "How do I get started?", "answer": "Sign up for a free account and follow the onboarding wizard to create your first project."}, {"question": "What payment methods do you accept?", "answer": "We accept all major credit cards, PayPal, and bank transfers."}, {"question": "Can I cancel anytime?", "answer": "Yes, you can cancel your subscription at any time with no penalties."}, {"question": "Is there a free trial?", "answer": "Yes, we offer a 14-day free trial with full access to all features."}]'>
 *     <me-heading slot="heading" variant="2">Common Questions</me-heading>
 *     <me-text slot="subheading" tag="p" variant="large">Find quick answers to your questions.</me-text>
 *     <me-button slot="action-primary" variant="secondary">Contact Support</me-button>
 *   </faq-type-2>
 */

@Component({
  tag: 'faq-type-2',
  styleUrl: 'index.scss',
  shadow: false,
})
export class FaqType2 {
  /**
   * The host element.
   */
  @Element() el!: HTMLElement;

  /**
   * List of FAQ entries rendered as expandable accordion items.
   * @content
   * @type {array}
   * @item.type {object}
   * @item.property {string} question - The question text displayed as the accordion trigger
   * @item.property {string} answer - The answer content revealed on expand (supports HTML)
   * @item.required question
   * @item.required answer
   * @item.content question answer
   * @minItems 4
   */
  @Prop() items: Array<{ question: string; answer: string }> = [];

  /**
   * Controls whether to show animations for accordion expand/collapse.
   * @type {boolean}
   * @default true
   * @example false - to disable all animations
   */
  @Prop() animated: boolean = true;

  /**
   * Accordion border style.
   * @type {string}
   * @default borderBottom
   * @example borderAll - to apply borders on all sides of accordion items
   */
  @Prop() borderStyle: 'borderBottom' | 'borderFull' = 'borderBottom';

  //State to manage the open index of the accordion
  @State() openIndex: number | null = null;

  // Initialize props from data attributes if not provided
  componentWillLoad() {
    if (!this.items) {
      const itemsAttr = this.el.getAttribute('items');
      if (itemsAttr) {
        try {
          this.items = JSON.parse(itemsAttr.replace(/&quot;/g, '"'));
        } catch (e) {
          console.error('Invalid JSON in data-items:', e);
        }
      }
    }
  }

  // Toggle the open/close state for an accordion item
  handleClick(index: number) {
    this.openIndex = this.openIndex === index ? null : index;
  }

  // Handle keyboard navigation for accordion items
  handleKeyDown(index: number, event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.handleClick(index);
    }
  }

  render() {
    const items = this.items;

    if (!items || items.length === 0) {
      console.warn('FAQ type-2 component requires at least one item');
      return null;
    }

    return (
      <me-section class={COMPONENT_NAME} yGap="5xl" yGapLg="7xl" xGapLg="5xl" xGap="lg" role="region" aria-label="Frequently Asked Questions">
        <me-row justify="center" yGap="none" yGapLg="none">
          <me-column span="12" spanLg="5">
            <div class={`${COMPONENT_NAME}__header`}>
              <slot name="heading"></slot>
              <slot name="subheading"></slot>
              <slot name="action-primary"></slot>
            </div>
          </me-column>
          <me-column>
            <div
              class={
                `${COMPONENT_NAME}__accordion ` +
                (this.animated ? ` ${COMPONENT_NAME}__accordion--animated ` : '') +
                (this.borderStyle === 'borderFull' ? `${COMPONENT_NAME}__accordion--border-full ` : '')
              }
              role="list"
              aria-label={`Frequently Asked Questions - Column`}
              itemScope
              itemType="https://schema.org/FAQPage"
            >
              {items.map((item, index) => (
                <div
                  class={`${COMPONENT_NAME}__item ` + (this.openIndex === index ? ` ${COMPONENT_NAME}__item--open` : '')}
                  role="listitem"
                  itemScope
                  itemType="https://schema.org/Question"
                >
                  <div
                    class={`${COMPONENT_NAME}__item-header`}
                    onClick={() => this.handleClick(index)}
                    aria-expanded={this.openIndex === index ? 'true' : 'false'}
                    aria-controls={`${COMPONENT_NAME}__content-${index}`}
                    id={`${COMPONENT_NAME}__item-header-${index}`}
                    tabIndex={0}
                    role="button"
                    onKeyDown={(e: KeyboardEvent) => {
                      this.handleKeyDown(index, e);
                    }}
                  >
                    <h3 class={`${COMPONENT_NAME}__item-title-heading`}>
                      <me-text tag="span" class={`${COMPONENT_NAME}__item-title`} weight="bold" variant="medium" itemProp="name">
                        {item.question}
                      </me-text>
                    </h3>
                    <span class={`${COMPONENT_NAME}__item-icon`} aria-hidden="true" />
                  </div>
                  <div
                    class={`${COMPONENT_NAME}__item-content`}
                    id={`${COMPONENT_NAME}__item-content-${index}`}
                    aria-labelledby={`${COMPONENT_NAME}__item-header-${index}`}
                    itemScope
                    itemType="https://schema.org/Answer"
                    itemProp="acceptedAnswer"
                  >
                    <div class={`${COMPONENT_NAME}__item-content-inner`}>
                      <me-text tag="p" innerHTML={item.answer} itemProp="text"></me-text>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </me-column>
        </me-row>
      </me-section>
    );
  }
}
